'use client';

import { c as _c } from "react/compiler-runtime";
import type { ChainId } from '@morpho-org/blue-sdk';
import type { OptionsStackingValue, SeriesOptionsType } from 'highcharts';
import type { Address } from 'viem';
import { formatLongString, type Time } from '@morpho-org/morpho-ts';
import { type OptionSelect, Select } from '@morpho-org/ui-v6/client';
import { Card } from '@morpho-org/ui-v6/server';
import { formatLltv, formatMarketName, UNKNOWN_SYMBOL } from '@morpho-org/ui-v6/utils';
import { SegmentedControlItem, SegmentedControlList, Tabs } from '@repo/ui';
import { abbreviateAddress } from '@repo/utils';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { formatUnits } from 'viem';
import type { MarketLabel } from '@/lib/transforms/marketLabel';
import type { TokenLabel } from '@/lib/transforms/tokenLabel';
import CumulativeChart from '@/components/charts/components/CumulativeChart';
import { colorAllocationSeries } from '@/components/charts/helpers/colorAllocationSeries';
import { type AllocationSeriesCustomData, formatTimeseriesData } from '@/components/charts/helpers/highcharts';
import { useHighchartsOptions } from '@/components/charts/hooks/useHighchartsOptions';
import { defaultTimeFrame, timeFrameOptions } from '@/config/table';
import { useIsMobile } from '@/hooks/useBreakpoints';
import type { AllocationAsset, AllocationSeries } from './allocationHistory.contract';
import { allocationHistoryQueryOptions, toHistoryRange } from './allocationHistory.query';
export interface ChartAllocationHistoryClientProps {
  address: Address;
  chainId: ChainId;
}
const getStackingOptions = (symbol: string): {
  segmented: {
    value: OptionsStackingValue;
    label: string;
  }[];
  select: OptionSelect<OptionsStackingValue>[];
} => ({
  segmented: [{
    value: 'normal',
    label: symbol
  }, {
    value: 'percent',
    label: '%'
  }],
  select: [{
    value: 'normal',
    label: symbol
  }, {
    value: 'percent',
    label: '%'
  }]
});
function getMarketSeriesName(market: MarketLabel, maxLength?: number): string {
  if (market.loanToken === null) return `Unknown Market ${abbreviateAddress(market.marketId)}`;
  const symbol = (token: TokenLabel) => formatLongString(token.symbol || UNKNOWN_SYMBOL, maxLength);
  if (market.collateralToken === null) {
    return market.loanToken.symbol || abbreviateAddress(market.loanToken.address);
  }
  return formatMarketName(symbol(market.loanToken), symbol(market.collateralToken));
}
function getMarketCustomData(market: MarketLabel): AllocationSeriesCustomData {
  return {
    marketName: getMarketSeriesName(market, 10),
    formattedLltv: market.collateralToken === null ? null : formatLltv(market.lltv),
    loanAssetLogoURI: market.loanToken?.logoUri ?? null,
    collateralAssetLogoURI: market.collateralToken?.logoUri ?? null,
    // An idle market lends the vault's asset against no collateral.
    isIdle: market.loanToken !== null && market.collateralToken === null
  };
}

/**
 * Every market of a V1 vault lends the vault's asset, so a market's loan token stands in for the
 * asset when Core's vault root is unavailable.
 */
function getVaultAsset(asset: AllocationAsset | null, series: AllocationSeries[]) {
  const loanToken = series.find(item => item.market.loanToken?.decimals != null)?.market.loanToken;
  return {
    symbol: asset?.symbol ?? loanToken?.symbol ?? null,
    decimals: asset?.decimals ?? loanToken?.decimals ?? null
  };
}

/**
 * The vault's supply into each market over the selected timeframe. The server hydrates the
 * default timeframe; another one keeps the previous range on screen in the chart's loading state
 * until it arrives, and a failed range shows the chart's error with the selectors still usable.
 * Without the asset's decimals the amounts can't be read, so the chart shows its error too. A
 * `partial` answer still charts the markets it has, and marks the card with `data-partial`.
 */
const ChartAllocationHistoryClient = t0 => {
  const $ = _c(55);
  const {
    address,
    chainId
  } = t0;
  const {
    palette
  } = useHighchartsOptions();
  const isMobile = useIsMobile();
  const [timeFrame, setTimeFrame] = useState(defaultTimeFrame);
  const [stackingMode, setStackingMode] = useState("normal");
  let t1;
  if ($[0] !== address || $[1] !== chainId || $[2] !== timeFrame) {
    t1 = allocationHistoryQueryOptions({
      chainId,
      address,
      range: toHistoryRange(timeFrame)
    });
    $[0] = address;
    $[1] = chainId;
    $[2] = timeFrame;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  let t2;
  if ($[4] !== t1) {
    t2 = {
      ...t1,
      placeholderData: keepPreviousData
    };
    $[4] = t1;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const {
    data,
    isError,
    isPlaceholderData,
    isPending
  } = useQuery(t2);
  const loading = isPending || isPlaceholderData && !isError;
  const [lastSymbol, setLastSymbol] = useState(null);
  let T0;
  let decimals;
  let formattedData;
  let symbol;
  let t10;
  let t11;
  let t3;
  let t4;
  let t5;
  let t6;
  let t7;
  let t8;
  let t9;
  let unit;
  if ($[6] !== data || $[7] !== isMobile || $[8] !== lastSymbol || $[9] !== palette || $[10] !== stackingMode) {
    const asset = data === undefined ? null : getVaultAsset(data.asset, data.series);
    if (asset !== null && asset.symbol !== lastSymbol) {
      setLastSymbol(asset.symbol);
    }
    symbol = asset?.symbol ?? lastSymbol;
    decimals = asset?.decimals ?? null;
    const stackingOptions = getStackingOptions(symbol ?? "");
    unit = stackingMode === "percent" ? "%" : symbol ?? "";
    const series = data === undefined || decimals === null ? [] : data.series.map(t12 => {
      const {
        market,
        points
      } = t12;
      return {
        type: "areaspline" as const,
        name: getMarketSeriesName(market),
        data: formatTimeseriesData(points.map(t13 => {
          const {
            timestamp,
            supplyAssets
          } = t13;
          return {
            x: timestamp,
            y: Number(formatUnits(BigInt(supplyAssets), decimals))
          };
        })),
        states: {
          inactive: {
            enabled: false
          }
        },
        custom: getMarketCustomData(market)
      };
    });
    formattedData = colorAllocationSeries(series, palette);
    T0 = Card;
    t7 = "gap-sm w-full flex-col";
    t8 = "m";
    t9 = true;
    t10 = "vault-v1-allocation-history-chart";
    t11 = data?.partial ? "true" : undefined;
    t5 = "flex items-center justify-between";
    if ($[25] === Symbol.for("react.memo_cache_sentinel")) {
      t6 = <span className="text-secondary-foreground text-xs">Allocation History</span>;
      $[25] = t6;
    } else {
      t6 = $[25];
    }
    t3 = "gap-sm flex flex-wrap items-center";
    t4 = symbol !== null && (isMobile ? <Select options={stackingOptions.select} value={stackingMode} onChange={setStackingMode} /> : <Tabs value={stackingMode} onValueChange={value => setStackingMode(value as OptionsStackingValue)}><SegmentedControlList size="default" aria-label="Unit">{stackingOptions.segmented.map(_temp)}</SegmentedControlList></Tabs>);
    $[6] = data;
    $[7] = isMobile;
    $[8] = lastSymbol;
    $[9] = palette;
    $[10] = stackingMode;
    $[11] = T0;
    $[12] = decimals;
    $[13] = formattedData;
    $[14] = symbol;
    $[15] = t10;
    $[16] = t11;
    $[17] = t3;
    $[18] = t4;
    $[19] = t5;
    $[20] = t6;
    $[21] = t7;
    $[22] = t8;
    $[23] = t9;
    $[24] = unit;
  } else {
    T0 = $[11];
    decimals = $[12];
    formattedData = $[13];
    symbol = $[14];
    t10 = $[15];
    t11 = $[16];
    t3 = $[17];
    t4 = $[18];
    t5 = $[19];
    t6 = $[20];
    t7 = $[21];
    t8 = $[22];
    t9 = $[23];
    unit = $[24];
  }
  let t12;
  if ($[26] !== timeFrame) {
    t12 = <Select options={timeFrameOptions} value={timeFrame} onChange={setTimeFrame} />;
    $[26] = timeFrame;
    $[27] = t12;
  } else {
    t12 = $[27];
  }
  let t13;
  if ($[28] !== t12 || $[29] !== t3 || $[30] !== t4) {
    t13 = <div className={t3}>{t4}{t12}</div>;
    $[28] = t12;
    $[29] = t3;
    $[30] = t4;
    $[31] = t13;
  } else {
    t13 = $[31];
  }
  let t14;
  if ($[32] !== t13 || $[33] !== t5 || $[34] !== t6) {
    t14 = <div className={t5}>{t6}{t13}</div>;
    $[32] = t13;
    $[33] = t5;
    $[34] = t6;
    $[35] = t14;
  } else {
    t14 = $[35];
  }
  const t15 = isError || data !== undefined && data.series.length > 0 && decimals === null;
  let t16;
  if ($[36] !== loading || $[37] !== t15) {
    t16 = {
      loading,
      error: t15
    };
    $[36] = loading;
    $[37] = t15;
    $[38] = t16;
  } else {
    t16 = $[38];
  }
  const t17 = symbol ?? undefined;
  let t18;
  if ($[39] === Symbol.for("react.memo_cache_sentinel")) {
    t18 = {
      chart: {
        plotBackgroundColor: "var(--muted)"
      }
    };
    $[39] = t18;
  } else {
    t18 = $[39];
  }
  let t19;
  if ($[40] !== formattedData || $[41] !== stackingMode || $[42] !== t16 || $[43] !== t17 || $[44] !== unit) {
    t19 = <CumulativeChart statuses={t16} unit={unit} stackingMode={stackingMode} tooltipMode="allocation" assetSymbol={t17} height="300px" series={formattedData} additionalOptions={t18} />;
    $[40] = formattedData;
    $[41] = stackingMode;
    $[42] = t16;
    $[43] = t17;
    $[44] = unit;
    $[45] = t19;
  } else {
    t19 = $[45];
  }
  let t20;
  if ($[46] !== T0 || $[47] !== t10 || $[48] !== t11 || $[49] !== t14 || $[50] !== t19 || $[51] !== t7 || $[52] !== t8 || $[53] !== t9) {
    t20 = <T0 className={t7} padding={t8} withBorder={t9} data-testid={t10} data-partial={t11}>{t14}{t19}</T0>;
    $[46] = T0;
    $[47] = t10;
    $[48] = t11;
    $[49] = t14;
    $[50] = t19;
    $[51] = t7;
    $[52] = t8;
    $[53] = t9;
    $[54] = t20;
  } else {
    t20 = $[54];
  }
  return t20;
};
export default ChartAllocationHistoryClient;
function _temp(option) {
  return <SegmentedControlItem key={option.value} value={option.value}>{option.label}</SegmentedControlItem>;
}